import { gql, useQuery } from '@apollo/client';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import {
  Badge,
  Button,
  Group,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import React, { useRef } from 'react';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { variantHomepageRouteConfig } from 'src/app/graph/routes';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import type {
  FilterBuildInput,
  LaunchBuildInputs_graph_variant_launch_buildInput_CompositionBuildInput,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';

import { LaunchIDHash } from '../../../LaunchIDHash';
import { SchemaHash } from '../launchInfo/SchemaHash';

const LAUNCH_BUILD_INPUTS_QUERY = gql`
  query LaunchBuildInputs(
    $graphId: ID!
    $graphVariant: String!
    $launchId: ID!
  ) {
    graph: service(id: $graphId) {
      id
      variant(name: $graphVariant) {
        id
        sourceVariant {
          id
          graphId
          name
        }
        launch(id: $launchId) {
          id
          completedAt
          buildInput {
            ... on CompositionBuildInput {
              subgraphs {
                hash
                name
                routingURL
                numberOfFields
                numberOfTypes
                updatedAt
                revision
              }
              version
            }
            ... on FilterBuildInput {
              filterConfig {
                exclude
                include
                hideUnreachableTypes
              }
              schemaHash
            }
          }
        }
      }
    }
  }
`;

export const BuildInputsModal = ({
  isOpen,
  launchId,
  graphId,
  graphVariant,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
  launchId: string;
  graphId: string;
  graphVariant: string;
}) => {
  const focusRef = useRef<HTMLButtonElement>(null);
  const { data, loading } = useQuery<
    GraphQLTypes.LaunchBuildInputs,
    GraphQLTypes.LaunchBuildInputsVariables
  >(LAUNCH_BUILD_INPUTS_QUERY, {
    variables: {
      graphId,
      graphVariant,
      launchId,
    },
  });

  const buildInput = data?.graph?.variant?.launch?.buildInput;
  if (!buildInput) return null;

  const sourceVariant: GraphRef = {
    graphId: data?.graph?.variant?.sourceVariant?.graphId as string,
    graphVariant: data?.graph?.variant?.sourceVariant?.name as string,
  };

  return (
    <Modal
      isOpen={isOpen}
      size="3xl"
      onClose={onClose}
      initialFocusRef={focusRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <div className="flex items-center">
            <ModalTitle className="flex-1 items-center">
              Launch{' '}
              <LaunchIDHash
                launchID={launchId}
                shortLaunchID={launchId.substring(0, 8)}
              />{' '}
              build inputs
            </ModalTitle>
            <div className="flex items-center rounded border border-primary bg-secondary px-2 py-1 text-base font-semibold">
              {graphId}@{graphVariant}
              <Badge className="ml-2">
                {buildInput.__typename === 'CompositionBuildInput'
                  ? 'Federated'
                  : buildInput.__typename === 'FilterBuildInput'
                    ? 'Contract'
                    : assertUnreachable(buildInput)}
              </Badge>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <Spinner />
          ) : (
            <>
              <div className="flex px-[--modal-body-padding-x] text-sm">
                <div className="flex-1">
                  {buildInput.__typename === 'CompositionBuildInput' ? (
                    <>
                      <span className="mr-2 font-semibold uppercase tracking-wide text-secondary">
                        Composition version
                      </span>
                      {buildInput.version}
                    </>
                  ) : buildInput.__typename === 'FilterBuildInput' ? (
                    <>
                      <span className="mr-2 font-semibold uppercase tracking-wide">
                        Source variant
                      </span>
                      <Link
                        to={variantHomepageRouteConfig.location({
                          graphId: sourceVariant.graphId,
                          graphVariant: sourceVariant.graphVariant,
                        })}
                      >
                        {data?.graph?.variant?.sourceVariant?.id}
                      </Link>
                    </>
                  ) : (
                    assertUnreachable(buildInput)
                  )}
                </div>
                {data?.graph?.variant?.launch?.completedAt && (
                  <div className="flex flex-none items-center">
                    <IconHistory className="mr-2 text-secondary" />
                    <LocalTimestamp
                      value={data.graph.variant.launch.completedAt}
                    />
                  </div>
                )}
              </div>
              {buildInput.__typename === 'CompositionBuildInput' ? (
                <CompositionBuildInputs input={buildInput} />
              ) : buildInput.__typename === 'FilterBuildInput' ? (
                <div>
                  <ContractBuildInputs input={buildInput} />
                </div>
              ) : (
                assertUnreachable(buildInput)
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} ref={focusRef}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

function CompositionBuildInputs({
  input,
}: {
  input: LaunchBuildInputs_graph_variant_launch_buildInput_CompositionBuildInput;
}) {
  return (
    <>
      <section className="px-[--modal-body-padding-x]">
        <Heading as="h3" size="lg">
          Subgraphs
        </Heading>
        <div className="text-secondary">
          These are the subgraphs that make up the build for your federated
          graph.{' '}
        </div>
      </section>
      <TableContainer>
        <Table size="condensed">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Version</Th>
              <Th>Routing URL</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(input.subgraphs ?? []).map((subgraph) => (
              <Tr key={subgraph.name}>
                <Td>
                  <Group gap={2}>
                    <IconSingleService className="text-icon-primary" />
                    {subgraph.name}
                  </Group>
                </Td>
                <Td>{subgraph.hash.substring(0, 6)}</Td>
                <Td>{subgraph.routingURL}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
}

function ContractBuildInputs({ input }: { input: FilterBuildInput }) {
  const showHideUnreachableTypes = useLDFlag(
    'mercury_show_hide_unreachable_types_toggle',
  );
  return (
    <>
      {/* Schema hash */}
      <div className="mb-6">
        <b className="mr-4">Schema hash</b>
        <SchemaHash hash={input.schemaHash} />
      </div>

      {/* Exclude filters */}
      <div className="mb-2">
        <b className="mr-4">Exclude filters</b>
        <span className="text-sm text-secondary">
          Schema elements with the following tags will be <b>excluded</b>.
        </span>
      </div>
      <div className="flex flex-wrap text-sm">
        {isMinLengthArray(1, input.filterConfig.exclude)
          ? input.filterConfig.exclude?.map((tag) => (
              <Badge key={tag} className="mr-2">
                {tag}
              </Badge>
            ))
          : 'No filters configured.'}
      </div>

      {/* Include filters */}
      <div className="mb-2 mt-6">
        <b className="mr-4">Include filters</b>
        <span className="text-sm text-secondary">
          Schema elements with the following tags will be <b>included</b>.
        </span>
      </div>
      <div className="flex flex-wrap text-sm">
        {isMinLengthArray(1, input.filterConfig.include)
          ? input.filterConfig.include?.map((tag) => (
              <Badge key={tag} className="mr-2">
                {tag}
              </Badge>
            ))
          : 'No filters configured.'}
      </div>

      {/* Hide unreachable types */}
      {showHideUnreachableTypes && (
        <>
          <div className="mb-2 mt-6">
            <b className="mr-4">Hide unreachable types</b>
            <span className="text-sm text-secondary">
              Types considered unreachable will be <b>excluded</b>.
            </span>
          </div>
          <div className="text-sm">
            <span className="rounded-sm border border-primary bg-silver px-2 py-px text-sm font-semibold text-secondary">
              {input.filterConfig.hideUnreachableTypes.toString()}
            </span>
          </div>
        </>
      )}
    </>
  );
}
